import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Alert,
  CircularProgress,
  Fab,
  useMediaQuery,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Autocomplete,
  Tabs,
  Tab,
  ListItemSecondaryAction,
  Badge,
  Link,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Devices as DevicesIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Share as ShareIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Build as InstallationIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  Star as StarIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { Calendar, momentLocalizer, Event, View } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useNavigate } from 'react-router-dom';
import { useApi, useAsyncOperation } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import { Appointment, Client, Device, User, TraccarDevice } from '../../types';
import { formatDate, formatTimeAgo, getStatusColor, getStatusLabel, to12h } from '../../utils/helpers';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Configurar localizer para moment
moment.locale('es');
const localizer = momentLocalizer(moment);

const timeSlots = [
  '08:00 - 09:00',
  '09:00 - 10:00',
  '10:00 - 11:00',
  '11:00 - 12:00',
  '12:00 - 13:00',
  '14:00 - 15:00',
  '15:00 - 16:00',
  '16:00 - 17:00',
  '17:00 - 18:00',
];

const validationSchema = Yup.object({
  scheduledDate: Yup.string().required('La fecha es requerida'),
  timeSlot: Yup.string().required('El horario es requerido'),
  technicianId: Yup.string().required('El técnico es requerido'),
  address: Yup.object({
    street: Yup.string().required('La dirección es requerida'),
    city: Yup.string().required('La ciudad es requerida'),
  }),
  deviceIds: Yup.array(), // OPCIONAL: Se pueden asignar después
});

interface AppointmentDialogProps {
  open: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onSubmit: (appointment: Partial<Appointment>) => void;
  onComplete?: (appointment: Appointment) => void; // Nueva prop para completar cita
  loading: boolean;
  technicians: User[];
}

const AppointmentDialog: React.FC<AppointmentDialogProps> = ({
  open,
  appointment,
  onClose,
  onSubmit,
  onComplete,
  loading,
  technicians,
}) => {
  const [selectedClientDevices, setSelectedClientDevices] = useState<TraccarDevice[]>([]);

  // Load not_installed devices
  const {
    data: notInstalledDevicesData,
    loading: notInstalledLoading,
    execute: fetchNotInstalledDevices,
  } = useApi(() => apiService.getNotInstalledDevices());

  // Load not_installed devices when dialog opens
  useEffect(() => {
    if (open) {
      fetchNotInstalledDevices();
    }
  }, [open]);

  // Update available devices when not_installed data loads
  useEffect(() => {
    if (notInstalledDevicesData) {
      setSelectedClientDevices(notInstalledDevicesData);
    }
  }, [notInstalledDevicesData]);

  const formik = useFormik({
    enableReinitialize: true, // ✅ Reinicializar cuando cambie appointment
    initialValues: {
      deviceIds: appointment?.deviceIds || [],
      scheduledDate: appointment?.scheduledDate || new Date().toISOString().split('T')[0],
      timeSlot: appointment?.timeSlot || '',
      technicianId: appointment?.technicianId || '',
      address: {
        street: appointment?.address?.street || '',
        city: appointment?.address?.city || '',
        state: appointment?.address?.state || '',
        zipCode: appointment?.address?.zipCode || '',
      },
      notes: appointment?.notes || '',
      clientNotes: appointment?.clientNotes || '',
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit({
        ...values,
        technicianName: technicians.find((t: any) => t._id === values.technicianId)?.name,
      });
    },
  });

  // ✅ Verificar si la cita está completada (solo lectura)
  const isCompleted = appointment?.status === 'completed';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          {appointment ? (isCompleted ? '📋 Detalles de Cita Completada' : 'Editar Cita') : 'Nueva Cita de Instalación'}
        </DialogTitle>
        {isCompleted && (
          <Alert severity="success" sx={{ mx: 3, mt: 2 }}>
            Esta cita ya fue completada y no puede ser modificada.
          </Alert>
        )}
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth disabled={isCompleted}>
                <InputLabel>Técnico Asignado</InputLabel>
                <Select
                  name="technicianId"
                  value={formik.values.technicianId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.technicianId && Boolean(formik.errors.technicianId)}
                  label="Técnico Asignado"
                >
                  {technicians.map((tech) => (
                    <MenuItem key={tech._id} value={tech._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: getTechnicianColor(tech._id), fontSize: '0.75rem' }}>
                          {tech.name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{tech.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tech.email}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                disabled={isCompleted}
                name="scheduledDate"
                label="Fecha de Instalación"
                type="date"
                value={formik.values.scheduledDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.scheduledDate && Boolean(formik.errors.scheduledDate)}
                helperText={formik.touched.scheduledDate && formik.errors.scheduledDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth disabled={isCompleted}>
                <InputLabel>Horario</InputLabel>
                <Select
                  name="timeSlot"
                  value={formik.values.timeSlot}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.timeSlot && Boolean(formik.errors.timeSlot)}
                  label="Horario"
                >
                  {timeSlots.map((slot) => (
                    <MenuItem key={slot} value={slot}>
                      {to12h(slot)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Dispositivos a Instalar (Opcional) {notInstalledLoading && '(Cargando...)'}
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Los dispositivos son opcionales. Puedes crear la cita sin dispositivos y asignarlos después cuando el técnico llegue al cliente.
              </Alert>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  disabled={isCompleted}
                  loading={notInstalledLoading}
                  options={selectedClientDevices}
                  getOptionLabel={(option) => {
                    const deviceName = option.name || option.imei;
                    const model = option.model && option.model !== 'N/A' ? option.model : '';
                    const status = option.status === 'online' ? '🟢' : '⚫';
                    return `${status} ${deviceName}${model ? ` - ${model}` : ''} (IMEI: ${option.imei})`;
                  }}
                  value={selectedClientDevices.filter(d =>
                    formik.values.deviceIds.includes(String(d.id)) ||
                    formik.values.deviceIds.includes(String(d.traccarId))
                  )}
                  onChange={(_, newValue) => {
                    // Use traccarId for the backend
                    formik.setFieldValue('deviceIds', newValue.map(d => String(d.traccarId || d.id)));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Seleccionar dispositivos del grupo Not_Installed (opcional)..."
                      error={formik.touched.deviceIds && Boolean(formik.errors.deviceIds)}
                      helperText={formik.touched.deviceIds && formik.errors.deviceIds}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={`${option.name || option.imei} ${option.status === 'online' ? '🟢' : '⚫'}`}
                        {...getTagProps({ index })}
                        key={String(option.id || option.traccarId)}
                      />
                    ))
                  }
                />
              </FormControl>
              {selectedClientDevices.length === 0 && !notInstalledLoading && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No hay dispositivos disponibles en el grupo "Not_Installed" (groupId: 4). Puedes crear la cita sin dispositivos.
                </Alert>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Dirección de Instalación
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                disabled={isCompleted}
                name="address.street"
                label="Dirección Completa"
                value={formik.values.address.street}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address?.street && Boolean(formik.errors.address?.street)}
                helperText={formik.touched.address?.street && formik.errors.address?.street}
                placeholder="Calle, número, sector, referencias..."
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                disabled={isCompleted}
                name="address.city"
                label="Ciudad"
                value={formik.values.address.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address?.city && Boolean(formik.errors.address?.city)}
                helperText={formik.touched.address?.city && formik.errors.address?.city}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                disabled={isCompleted}
                name="address.state"
                label="Provincia"
                value={formik.values.address.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                disabled={isCompleted}
                name="notes"
                label="Notas para el Técnico"
                multiline
                rows={3}
                value={formik.values.notes}
                onChange={formik.handleChange}
                placeholder="Instrucciones especiales, herramientas necesarias, etc..."
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                disabled={isCompleted}
                name="clientNotes"
                label="Notas del Cliente"
                multiline
                rows={2}
                value={formik.values.clientNotes}
                onChange={formik.handleChange}
                placeholder="Comentarios adicionales del cliente..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: isCompleted ? 'flex-end' : 'space-between', px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            {isCompleted ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!isCompleted && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {appointment && onComplete && (appointment.status === 'scheduled' || appointment.status === 'in_progress') && (
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => {
                    onClose();
                    onComplete(appointment);
                  }}
                  disabled={loading}
                  startIcon={<CheckIcon />}
                >
                  Completar y Cerrar
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !formik.isValid}
                startIcon={loading ? <CircularProgress size={20} /> : <ScheduleIcon />}
              >
                {loading ? 'Guardando...' : appointment ? 'Actualizar Cita' : 'Programar Cita'}
              </Button>
            </Box>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Technicians will be loaded from API (users with tech_ prefix)

// Generate colors for technicians based on their ID - moved outside to avoid re-creation
const getTechnicianColor = (technicianId: string) => {
  const colors = ['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#607D8B', '#795548', '#FF5722'];
  const index = technicianId.length % colors.length;
  return colors[index];
};

// Se nombra a QUIÉN le llegó el dinero, no solo el instrumento: una transferencia a la
// cuenta del técnico se la debe a la empresa igual que el efectivo.
type MetodoCobro = 'none' | 'cash' | 'transfer_technician' | 'transfer_company';

export const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('month');
  const isMobile = useMediaQuery('(max-width:900px)');
  // En móvil, la vista "agenda" (lista) es mucho más usable que "mes"
  useEffect(() => {
    if (isMobile) setView('agenda');
  }, [isMobile]);
  const [date, setDate] = useState(new Date());
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<Event[]>([]);
  const [currentTab, setCurrentTab] = useState(0); // 0 = Citas, 1 = Instalaciones
  const [completeInstallationDialog, setCompleteInstallationDialog] = useState<{
    open: boolean;
    appointment: Appointment | null;
    quick?: boolean;
    technicianId?: string;
    scheduledDate?: string;
  }>({
    open: false,
    appointment: null,
  });

  const [closeAppointmentData, setCloseAppointmentData] = useState({
    clientId: '',
    completionNotes: '',
    deviceIds: [] as number[], // TraccarIds de dispositivos seleccionados
    deviceNames: [] as string[],
    // Campos de pago del cliente
    paymentMethod: 'none' as MetodoCobro,
    paymentAmount: '',
    paymentReference: ''
  });

  const [clientSelectionTab, setClientSelectionTab] = useState(0); // 0 = Crear Nuevo, 1 = Seleccionar Existente
  const [newClientData, setNewClientData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: ''
  });

  // Confirmar una cita tentativa (asignar técnico → pasa a agendada)
  const [confirmTentativeDialog, setConfirmTentativeDialog] = useState<{ open: boolean; appt: any | null }>({ open: false, appt: null });
  const [tentTechnicianId, setTentTechnicianId] = useState('');
  const [tentTime, setTentTime] = useState('');
  const [tentLocation, setTentLocation] = useState('');
  const [tentSaving, setTentSaving] = useState(false);
  // Editar una cita tentativa (reprogramar) sin confirmarla
  const [editTentDialog, setEditTentDialog] = useState<{ open: boolean; appt: any | null }>({ open: false, appt: null });
  const [editTentForm, setEditTentForm] = useState({ scheduledDate: '', timeSlot: '', leadName: '', leadPhone: '', notes: '' });
  const [editTentSaving, setEditTentSaving] = useState(false);
  // Confirmar pago del cliente (post-instalación). Una vez confirmado, se bloquea.
  const [payDialog, setPayDialog] = useState<{ open: boolean; installation: any | null }>({ open: false, installation: null });
  const [payMethod, setPayMethod] = useState<'cash' | 'bank_transfer'>('cash');
  const [payAmount, setPayAmount] = useState('4990');
  const [payReference, setPayReference] = useState('');
  const [paySaving, setPaySaving] = useState(false);

  // Load appointments from API
  const {
    data: appointmentsData,
    loading: appointmentsLoading,
    error: appointmentsError,
    execute: fetchAppointments,
  } = useApi(() => apiService.getAppointments({ limit: 100000 }));

  // Stabilize appointments array to prevent unnecessary re-renders
  const appointments = useMemo(() => {
    // appointmentsData is already the array, not an object with .data property
    return appointmentsData || [];
  }, [appointmentsData]);

  // Load technicians from API (users with tech_ prefix)
  const {
    data: techniciansData,
    loading: techniciansLoading,
    error: techniciansError,
    execute: fetchTechnicians,
  } = useApi(() => apiService.getTechnicians());

  // Stabilize technicians array to prevent unnecessary re-renders
  const technicians = useMemo(() => {
    const techData = (techniciansData as any)?.users || [];
    // Filter users with role technician
    return techData.filter((user: any) => user.role === 'technician');
  }, [techniciansData]);

  // Load installations from API
  const {
    data: installationsData,
    loading: installationsLoading,
    error: installationsError,
    execute: fetchInstallations,
  } = useApi(() => apiService.getInstallations(1, 100000));

  // Stabilize installations array
  const installations = useMemo(() => {
    return installationsData?.installations || [];
  }, [installationsData?.installations]);

  // Load clients for appointment closing
  const {
    data: clientsData,
    loading: clientsLoading,
    execute: fetchClients,
  } = useApi(() => apiService.getClients(1, 1000));

  const clients = useMemo(() => {
    console.log('👥 clientsData:', clientsData);
    const clientsList = clientsData?.clients || [];
    console.log('👥 Total clients:', clientsList.length);
    return clientsList;
  }, [clientsData?.clients]);

  // Load not_installed devices for appointment closure
  const {
    data: notInstalledDevicesData,
    loading: notInstalledLoading,
    execute: fetchNotInstalledDevices,
  } = useApi(() => apiService.getNotInstalledDevices(), { immediate: false });

  const notInstalledDevices = useMemo(() => {
    return notInstalledDevicesData || [];
  }, [notInstalledDevicesData]);

  // Equipos agregados a mano por IMEI (traídos de Traccar aunque no estén en el grupo stock).
  const [extraDevices, setExtraDevices] = useState<any[]>([]);
  const [imeiToAdd, setImeiToAdd] = useState('');
  const [addingImei, setAddingImei] = useState(false);

  // Lista que ve el selector de la Instalación Rápida: stock + los agregados por IMEI, sin repetir.
  const availableDevices = useMemo(() => {
    const map = new Map<any, any>();
    [...notInstalledDevices, ...extraDevices].forEach((d: any) => {
      map.set(d.traccarId ?? d.id, d);
    });
    return Array.from(map.values());
  }, [notInstalledDevices, extraDevices]);

  const { execute: createAppointment, loading: creating } = useAsyncOperation();
  const { execute: updateAppointment, loading: updating } = useAsyncOperation();
  const { execute: completeInstallation, loading: completing } = useAsyncOperation();

  // Load appointments, technicians, installations and clients on component mount
  useEffect(() => {
    fetchAppointments();
    fetchTechnicians();
    fetchInstallations();
    fetchClients();
  }, []);

  // State for showing assignment results
  const [assignmentAlert, setAssignmentAlert] = useState<{
    show: boolean;
    type: 'success' | 'warning' | 'error' | 'info';
    message: string;
    details?: string;
  }>({ show: false, type: 'success', message: '' });

  // Auto-hide alert after 7 seconds
  useEffect(() => {
    if (assignmentAlert.show) {
      const timer = setTimeout(() => {
        setAssignmentAlert(prev => ({ ...prev, show: false }));
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [assignmentAlert.show]);

  // Al cerrar el diálogo de instalación, limpiar los equipos agregados a mano por IMEI.
  useEffect(() => {
    if (!completeInstallationDialog.open) {
      setExtraDevices([]);
      setImeiToAdd('');
    }
  }, [completeInstallationDialog.open]);

  // Convert appointments to calendar events using useMemo to avoid infinite loops
  const calendarEventsData = useMemo(() => {
    return appointments.map((apt: Appointment) => {
      const technician = technicians.find((t: any) => t._id === apt.technicianId);
      const [startTime] = (apt.timeSlot || '').split(' - ');

      // Extract date part from ISO string (YYYY-MM-DD)
      const scheduledDateOnly = typeof apt.scheduledDate === 'string'
        ? apt.scheduledDate.split('T')[0]
        : moment(apt.scheduledDate).format('YYYY-MM-DD');

      // Las tentativas pueden no tener hora ("Por confirmar"): usar la fecha directa
      const validTime = /^\d{1,2}:\d{2}/.test(startTime || '');
      const startDateTime = validTime
        ? moment(`${scheduledDateOnly} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate()
        : moment(apt.scheduledDate).toDate();
      const endDateTime = moment(startDateTime).add(1, 'hour').toDate();

      const isTentative = (apt as any).status === 'tentative';
      const leadDisplay = (apt.clientId as Client)?.name || (apt as any).leadName || 'Cliente';
      return {
        id: apt._id,
        title: isTentative
          ? `🟡 ${leadDisplay} (tentativa)`
          : `${leadDisplay} - ${technician?.name || 'Técnico'}`,
        start: startDateTime,
        end: endDateTime,
        resource: apt,
        style: {
          backgroundColor: isTentative ? '#FFB300' : (technician ? getTechnicianColor(technician._id) : '#2196F3'),
        },
      };
    });
  }, [appointments, technicians]);

  // Update calendar events when data changes
  useEffect(() => {
    setCalendarEvents(calendarEventsData);
  }, [calendarEventsData]);

  const handleSelectEvent = (event: Event) => {
    setSelectedAppointment(event.resource as Appointment);
    setAppointmentDialogOpen(true);
  };

  const handleSelectSlot = ({ start }: { start: Date; end: Date }) => {
    setSelectedAppointment(null);
    setAppointmentDialogOpen(true);
    // You could pre-fill the date based on the selected slot
  };

  const handleAppointmentSubmit = async (appointmentData: Partial<Appointment>) => {
    try {
      if (selectedAppointment && selectedAppointment._id) {
        // Update existing appointment
        const result = await updateAppointment(() =>
          apiService.updateAppointment(selectedAppointment._id!, appointmentData)
        );
        if (result) {
          console.log('✅ Cita actualizada exitosamente');

          // Check if devices were added/updated
          if (result.data?.deviceAssignments && result.data.deviceAssignments.length > 0) {
            const successfulAssignments = result.data.deviceAssignments.filter((a: any) => a.status === 'success');
            setAssignmentAlert({
              show: true,
              type: 'success',
              message: 'Cita actualizada exitosamente',
              details: `${successfulAssignments.length} dispositivos asignados automáticamente al técnico.`
            });
          } else {
            setAssignmentAlert({
              show: true,
              type: 'success',
              message: 'Cita actualizada exitosamente',
              details: result.message || 'Los cambios han sido guardados.'
            });
          }
        }
      } else {
        // Create new appointment with automatic device assignment
        const result = await createAppointment(() =>
          apiService.createAppointment(appointmentData)
        );

        if (result) {
          console.log('✅ Cita creada exitosamente');

          // Check if the response includes device assignment results
          if (result.data?.deviceAssignments) {
            const successfulAssignments = result.data.deviceAssignments.filter((a: any) => a.status === 'success');
            const failedAssignments = result.data.deviceAssignments.filter((a: any) => a.status === 'error');

            if (failedAssignments.length > 0) {
              setAssignmentAlert({
                show: true,
                type: 'warning',
                message: 'Cita creada con advertencias',
                details: `${successfulAssignments.length} dispositivos asignados correctamente, pero ${failedAssignments.length} no pudieron ser asignados automáticamente al técnico.`
              });
            } else if (successfulAssignments.length > 0) {
              setAssignmentAlert({
                show: true,
                type: 'success',
                message: 'Cita creada exitosamente',
                details: `${successfulAssignments.length} dispositivos asignados automáticamente al técnico ${appointmentData.technicianName || 'seleccionado'}.`
              });
            } else {
              // No devices assigned
              setAssignmentAlert({
                show: true,
                type: 'info',
                message: 'Cita creada sin dispositivos',
                details: 'La cita fue creada exitosamente. Recuerda asignar dispositivos antes de completar la instalación.'
              });
            }
          } else {
            setAssignmentAlert({
              show: true,
              type: 'success',
              message: result.message || 'Cita creada exitosamente',
              details: 'La cita ha sido programada correctamente.'
            });
          }
        }
      }

      setAppointmentDialogOpen(false);
      setSelectedAppointment(null);

      // Refresh appointments list
      fetchAppointments();

    } catch (error) {
      console.error('❌ Error saving appointment:', error);
    }
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Extrae lat,lng de un link de Google Maps pegado o de un texto "18.53, -69.88".
  // Los links cortos (maps.app.goo.gl) no traen coords → devuelve null (pide pegar coords).
  const parseLatLng = (str: string): { lat: number; lng: number } | null => {
    if (!str) return null;
    const m = String(str).match(/(-?\d{1,3}\.\d+)[,\s]+(-?\d{1,3}\.\d+)/);
    if (!m) return null;
    const lat = parseFloat(m[1]); const lng = parseFloat(m[2]);
    if (!isFinite(lat) || !isFinite(lng)) return null;
    return { lat, lng };
  };

  // Confirmar una cita tentativa: asignar técnico y pasarla a agendada
  const openConfirmTentative = (appt: any) => {
    setTentTechnicianId('');
    // Prefill de hora (si la tentativa ya tenía una hora) y de ubicación (mapa o coords).
    const rawTime = appt?.timeSlot && /^\d{1,2}:\d{2}$/.test(appt.timeSlot) ? appt.timeSlot : '';
    setTentTime(rawTime);
    const coords = appt?.address?.coordinates;
    setTentLocation(appt?.mapsLink || (coords ? `${coords.lat}, ${coords.lng}` : ''));
    setConfirmTentativeDialog({ open: true, appt });
  };

  const handleConfirmTentative = async () => {
    const appt = confirmTentativeDialog.appt;
    if (!appt || !tentTechnicianId) return;
    setTentSaving(true);
    try {
      const coords = parseLatLng(tentLocation);
      await apiService.confirmTentativeAppointment(appt._id, {
        technicianId: tentTechnicianId,
        timeSlot: tentTime || undefined,
        ...(coords ? { address: { coordinates: coords } } : {}),
      });
      setConfirmTentativeDialog({ open: false, appt: null });
      setAssignmentAlert({ show: true, type: 'success', message: 'Cita confirmada', details: 'La cita tentativa pasó a agendada. Ya puedes completar la instalación cuando corresponda.' });
      fetchAppointments();
    } catch (e: any) {
      setAssignmentAlert({ show: true, type: 'error', message: 'Error al confirmar', details: e?.response?.data?.message || e?.message });
    } finally {
      setTentSaving(false);
    }
  };

  const handleDeleteTentative = async (appt: any) => {
    try {
      await apiService.deleteAppointment(appt._id);
      setAssignmentAlert({ show: true, type: 'success', message: 'Cita tentativa eliminada' });
      fetchAppointments();
    } catch (e: any) {
      setAssignmentAlert({ show: true, type: 'error', message: 'Error al eliminar', details: e?.response?.data?.message || e?.message });
    }
  };

  // Abrir el diálogo de editar/reprogramar una tentativa, pre-llenando con sus datos
  const openEditTentative = (appt: any) => {
    const d = appt.scheduledDate ? new Date(appt.scheduledDate) : null;
    const dateStr = d && !isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : '';
    const rawTime = appt.timeSlot && /^\d{1,2}:\d{2}/.test(appt.timeSlot) ? appt.timeSlot : '';
    setEditTentForm({
      scheduledDate: dateStr,
      timeSlot: rawTime,
      leadName: appt.leadName || appt.clientId?.name || '',
      leadPhone: appt.leadPhone || '',
      notes: appt.notes || '',
    });
    setEditTentDialog({ open: true, appt });
  };

  const handleEditTentative = async () => {
    const appt = editTentDialog.appt;
    if (!appt || !editTentForm.scheduledDate) return;
    setEditTentSaving(true);
    try {
      await apiService.updateTentativeAppointment(appt._id, {
        scheduledDate: editTentForm.scheduledDate,
        timeSlot: editTentForm.timeSlot || undefined,
        leadName: editTentForm.leadName,
        leadPhone: editTentForm.leadPhone,
        notes: editTentForm.notes,
      });
      setEditTentDialog({ open: false, appt: null });
      setAssignmentAlert({ show: true, type: 'success', message: 'Cita tentativa actualizada' });
      fetchAppointments();
    } catch (e: any) {
      setAssignmentAlert({ show: true, type: 'error', message: 'Error al actualizar', details: e?.response?.data?.message || e?.message });
    } finally {
      setEditTentSaving(false);
    }
  };

  // Confirmar el pago del cliente de una instalación completada (efectivo/transferencia)
  const openConfirmPayment = (installation: any) => {
    setPayMethod('cash');
    setPayAmount(String(installation?.paymentInfo?.amount || 4990));
    setPayReference('');
    setPayDialog({ open: true, installation });
  };

  const handleConfirmPayment = async () => {
    const inst = payDialog.installation;
    const amount = parseFloat(payAmount);
    if (!inst || !amount || amount <= 0) return;
    setPaySaving(true);
    try {
      await apiService.confirmClientPayment(inst._id, {
        paymentMethod: payMethod,
        paymentAmount: amount,
        paymentReference: payReference || undefined,
      });
      setPayDialog({ open: false, installation: null });
      setAssignmentAlert({ show: true, type: 'success', message: 'Pago confirmado', details: `${payMethod === 'cash' ? 'Efectivo' : 'Transferencia'} RD$${amount.toLocaleString('es-DO')} registrado.` });
      fetchInstallations();
    } catch (e: any) {
      setAssignmentAlert({ show: true, type: 'error', message: 'Error al confirmar pago', details: e?.response?.data?.message || e?.message });
    } finally {
      setPaySaving(false);
    }
  };

  // Handle complete installation
  const handleCompleteInstallation = (appointment: Appointment) => {
    // ✅ YA NO SE VALIDAN DISPOSITIVOS AQUÍ
    // El técnico seleccionará los dispositivos en el diálogo de cierre

    // Cargar dispositivos disponibles (not_installed)
    fetchNotInstalledDevices();

    // Inicializar con un dispositivo por defecto (el técnico puede agregar más)
    setCloseAppointmentData(prev => ({
      ...prev,
      deviceIds: [], // Se seleccionarán en el diálogo
      deviceNames: [] // Se ajustará según dispositivos seleccionados
    }));

    setCompleteInstallationDialog({
      open: true,
      appointment,
    });
  };

  // Agregar un equipo al selector buscándolo por IMEI (lo trae de Traccar si no está en stock).
  const handleAddDeviceByImei = async () => {
    const imei = imeiToAdd.trim();
    if (imei.length < 4) {
      setAssignmentAlert({ show: true, type: 'error', message: 'IMEI inválido', details: 'Escribe al menos 4 dígitos del IMEI' });
      return;
    }
    try {
      setAddingImei(true);
      const res: any = await apiService.resolveDeviceByImei(imei);
      const device = res?.data;
      if (!res?.success || !device) {
        setAssignmentAlert({ show: true, type: 'error', message: 'No se encontró', details: res?.message || 'No se pudo resolver el IMEI' });
        return;
      }
      const tid = device.traccarId ?? device.id;
      // Agregar a las opciones si no estaba
      setExtraDevices(prev => (prev.some((d: any) => (d.traccarId ?? d.id) === tid) ? prev : [...prev, device]));
      // Auto-seleccionarlo
      setCloseAppointmentData(prev => {
        if (prev.deviceIds.includes(tid)) return prev;
        return {
          ...prev,
          deviceIds: [...prev.deviceIds, tid],
          deviceNames: [...prev.deviceNames, device.name || ''],
        };
      });
      setImeiToAdd('');
      if (res.assigned) {
        setAssignmentAlert({ show: true, type: 'warning', message: 'Equipo ya asignado', details: `Este equipo ya figura a nombre de ${res.assignedTo}. Para instalarlo a otro cliente hay que liberarlo primero.` });
      } else {
        setAssignmentAlert({ show: true, type: 'success', message: 'Equipo agregado', details: `IMEI ${device.imei} agregado a la lista${res.imported ? ' (importado de Traccar)' : ''}.` });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Error buscando el equipo';
      setAssignmentAlert({ show: true, type: 'error', message: 'No se pudo agregar', details: msg });
    } finally {
      setAddingImei(false);
    }
  };

  const handleCompleteInstallationSubmit = async () => {
    const isQuick = completeInstallationDialog.quick && !completeInstallationDialog.appointment;
    if (!isQuick && (!completeInstallationDialog.appointment || !completeInstallationDialog.appointment._id)) return;

    try {
      // En modo rápido (sin cita previa) validar que se eligió técnico
      if (isQuick && !completeInstallationDialog.technicianId) {
        setAssignmentAlert({
          show: true,
          type: 'error',
          message: 'Error',
          details: 'Debe seleccionar el técnico que realizó la instalación'
        });
        return;
      }

      // Validar según el tab seleccionado
      if (clientSelectionTab === 0) {
        // Tab "Crear Nuevo Cliente" - validar datos
        if (!newClientData.name || !newClientData.email) {
          setAssignmentAlert({
            show: true,
            type: 'error',
            message: 'Error',
            details: 'Nombre y email son requeridos para crear el cliente'
          });
          return;
        }
      } else {
        // Tab "Seleccionar Existente" - validar selección
        if (!closeAppointmentData.clientId) {
          setAssignmentAlert({
            show: true,
            type: 'error',
            message: 'Error',
            details: 'Debe seleccionar un cliente existente'
          });
          return;
        }
      }

      // Validar nombres de dispositivos
      const emptyNames = closeAppointmentData.deviceNames.filter(name => !name || name.trim() === '');
      if (emptyNames.length > 0) {
        setAssignmentAlert({
          show: true,
          type: 'error',
          message: 'Error',
          details: 'Debe proporcionar nombres para todos los dispositivos'
        });
        return;
      }

      // Validar que se hayan seleccionado dispositivos
      if (!closeAppointmentData.deviceIds || closeAppointmentData.deviceIds.length === 0) {
        setAssignmentAlert({
          show: true,
          type: 'error',
          message: 'Error',
          details: 'Debe seleccionar al menos un dispositivo para cerrar la cita'
        });
        return;
      }

      // Preparar datos para el backend
      const closeData: any = {
        completionNotes: closeAppointmentData.completionNotes,
        deviceIds: closeAppointmentData.deviceIds, // ✅ Incluir deviceIds seleccionados
        deviceNames: closeAppointmentData.deviceNames,
        // Datos de pago del cliente
        paymentMethod: closeAppointmentData.paymentMethod,
        paymentAmount: closeAppointmentData.paymentAmount ? parseFloat(closeAppointmentData.paymentAmount) : 0,
        paymentReference: closeAppointmentData.paymentReference || undefined
      };

      console.log('🔍 DEBUG - deviceIds seleccionados:', closeAppointmentData.deviceIds);
      console.log('   Tipo de deviceIds[0]:', typeof closeAppointmentData.deviceIds[0]);
      console.log('   Dispositivos disponibles:', notInstalledDevices.map((d: any) => ({
        id: d.id,
        traccarId: d.traccarId,
        imei: d.imei
      })));

      // Si es nuevo cliente, enviar datos de newClient; si es existente, enviar clientId
      if (clientSelectionTab === 0) {
        console.log('🆕 Enviando datos de nuevo cliente al backend:', newClientData);
        closeData.newClient = {
          name: newClientData.name,
          email: newClientData.email,
          phoneNumber: newClientData.phoneNumber,
          address: newClientData.address,
          city: newClientData.city
        };
      } else {
        console.log('🔍 Enviando cliente existente:', closeAppointmentData.clientId);
        closeData.clientId = closeAppointmentData.clientId;
      }

      console.log('🔒 Cerrando cita con datos:', closeData);
      console.log('📱 Nombres de dispositivos:', closeData.deviceNames);

      // Determinar el ID de la cita a cerrar.
      // Modo rápido: crear una cita mínima al vuelo (con el técnico elegido) y cerrarla enseguida.
      let appointmentId = completeInstallationDialog.appointment?._id;
      if (isQuick) {
        const technician: any = technicians.find((t: any) => t._id === completeInstallationDialog.technicianId);
        const now = new Date();
        const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const apptRes: any = await createAppointment(() =>
          apiService.createAppointment({
            technicianId: completeInstallationDialog.technicianId,
            technicianName: technician?.name,
            scheduledDate: completeInstallationDialog.scheduledDate || new Date().toISOString().split('T')[0],
            timeSlot: `Rápida ${hhmm}`,
            address: { street: 'Instalación rápida', city: 'N/A' },
          } as any)
        );
        appointmentId = apptRes?.appointment?._id || apptRes?.data?.appointment?._id;
        if (!appointmentId) {
          setAssignmentAlert({
            show: true,
            type: 'error',
            message: 'Error',
            details: 'No se pudo crear la cita base para la instalación rápida'
          });
          return;
        }
      }

      // Cerrar la cita - el backend se encarga de crear el cliente DESPUÉS de Traccar
      const result = await completeInstallation(() =>
        apiService.closeAppointment(
          appointmentId!,
          closeData
        )
      );

      // Validar que el resultado sea exitoso
      if (!result || !result.success) {
        const errorMessage = result?.message || 'Error desconocido al cerrar la cita';
        setAssignmentAlert({
          show: true,
          type: 'error',
          message: 'Error cerrando la cita',
          details: errorMessage
        });
        return;
      }

      // Verificar que se creó usuario Traccar si era necesario
      if (!result.data?.traccarUserCreated && result.data?.traccarUserCreated === false) {
        console.warn('⚠️ No se pudo crear usuario Traccar');
      }

      console.log('✅ Cita cerrada exitosamente:', result);

      // Cerrar diálogo y limpiar estados
      setCompleteInstallationDialog({ open: false, appointment: null });
      setCloseAppointmentData({ clientId: '', completionNotes: '', deviceIds: [], deviceNames: [], paymentMethod: 'none', paymentAmount: '', paymentReference: '' });
      setNewClientData({ name: '', email: '', phoneNumber: '', address: '', city: '' });
      setClientSelectionTab(0);

      // Refresh data
      fetchAppointments();
      fetchInstallations();
      fetchClients();

      // Show success message with details
      const details = [];
      if (clientSelectionTab === 0) {
        details.push('Cliente nuevo creado');
      }
      if (result.data?.traccarUserCreated) {
        details.push('Usuario Traccar creado');
      } else {
        details.push('Cliente ya tenía usuario Traccar');
      }
      if (result.data?.emailSent) {
        details.push('Credenciales enviadas por email');
      }
      if (result.data?.deviceTransfers) {
        const successfulTransfers = result.data.deviceTransfers.filter((t: any) => t.status === 'success').length;
        details.push(`${successfulTransfers} dispositivo(s) transferido(s) al cliente`);
      }

      setAssignmentAlert({
        show: true,
        type: 'success',
        message: result.message || 'Cita cerrada exitosamente',
        details: details.join(', ')
      });

    } catch (error: any) {
      console.error('❌ Error closing appointment:', error);
      setAssignmentAlert({
        show: true,
        type: 'error',
        message: 'Error cerrando la cita',
        details: error.response?.data?.message || error.message || 'Error desconocido'
      });
    }
  };

  const eventStyleGetter = (event: Event) => {
    const appointment = event.resource as Appointment;
    const technician = technicians.find((t: any) => t._id === appointment.technicianId);

    let backgroundColor = technician ? getTechnicianColor(technician._id) : '#2196F3';
    let borderColor = backgroundColor;

    // Change color based on status
    switch (appointment.status) {
      case 'tentative':
        backgroundColor = '#FFB300';
        borderColor = '#FF8F00';
        break;
      case 'completed':
        backgroundColor = '#4CAF50';
        borderColor = '#388E3C';
        break;
      case 'in_progress':
        backgroundColor = '#FF9800';
        borderColor = '#F57C00';
        break;
      case 'cancelled':
        backgroundColor = '#F44336';
        borderColor = '#D32F2F';
        break;
      case 'no_show':
        backgroundColor = '#9E9E9E';
        borderColor = '#616161';
        break;
    }

    return {
      style: {
        backgroundColor,
        borderColor,
        color: 'white',
        border: `2px solid ${borderColor}`,
        borderRadius: '4px',
      },
    };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'tentative': return 'warning';
      case 'scheduled': return 'primary';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'no_show': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'tentative': return 'Tentativa';
      case 'scheduled': return 'Programada';
      case 'in_progress': return 'En Progreso';
      case 'completed': return 'Completada';
      case 'cancelled': return 'Cancelada';
      case 'no_show': return 'No Asistió';
      default: return status;
    }
  };

  const generateCalendarLink = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const baseUrl = window.location.origin;
    const calendarUrl = `${baseUrl}/calendar/shared?month=${nextMonth.getFullYear()}-${(nextMonth.getMonth() + 1).toString().padStart(2, '0')}`;

    navigator.clipboard.writeText(calendarUrl);
    // You could show a success message here
  };

  return (
    <Box>
      {/* Assignment Result Alert */}
      {assignmentAlert.show && (
        <Alert
          severity={assignmentAlert.type}
          onClose={() => setAssignmentAlert({ ...assignmentAlert, show: false })}
          sx={{ mb: 3 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {assignmentAlert.message}
          </Typography>
          {assignmentAlert.details && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {assignmentAlert.details}
            </Typography>
          )}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Calendario de Instalaciones
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Programa y gestiona las citas de instalación con técnicos
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={generateCalendarLink}
          >
            Compartir Calendario
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<InstallationIcon />}
            onClick={() => {
              fetchNotInstalledDevices();
              setCloseAppointmentData({ clientId: '', completionNotes: '', deviceIds: [], deviceNames: [], paymentMethod: 'none', paymentAmount: '', paymentReference: '' });
              setNewClientData({ name: '', email: '', phoneNumber: '', address: '', city: '' });
              setClientSelectionTab(clients.length > 0 ? 1 : 0);
              setCompleteInstallationDialog({ open: true, appointment: null, quick: true, technicianId: '', scheduledDate: new Date().toISOString().split('T')[0] });
            }}
            size="large"
          >
            Instalación Rápida
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedAppointment(null);
              setAppointmentDialogOpen(true);
            }}
            size="large"
          >
            Nueva Cita
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label="Citas"
            icon={<CalendarIcon />}
            iconPosition="start"
          />
          <Tab
            label="Instalaciones"
            icon={<InstallationIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <>
          {/* Tentativas por confirmar — red de seguridad para no perder leads agendados */}
          {appointments.filter((a: any) => a.status === 'tentative').length > 0 && (
            <Paper sx={{ p: 2, mb: 3, borderLeft: '5px solid #FFB300', bgcolor: 'rgba(255,179,0,0.06)' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                🟡 Tentativas por confirmar
                <Chip size="small" color="warning" label={appointments.filter((a: any) => a.status === 'tentative').length} />
              </Typography>
              <List dense disablePadding>
                {appointments
                  .filter((a: any) => a.status === 'tentative')
                  .sort((a: any, b: any) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
                  .map((appt: any) => (
                    <ListItem
                      key={appt._id}
                      sx={{ px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' }, flexWrap: 'wrap', gap: 1 }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {appt.leadPhone && (
                            <Tooltip title="Abrir chat en el CRM">
                              <IconButton
                                size="small"
                                sx={{ color: '#25D366' }}
                                onClick={() => {
                                  const digits = String(appt.leadPhone).replace(/\D/g, '');
                                  const waId = digits.length === 10 ? `1${digits}` : digits;
                                  navigate(`/bot/leads?phone=${encodeURIComponent(waId)}`);
                                }}
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Editar / reprogramar">
                            <IconButton size="small" color="primary" onClick={() => openEditTentative(appt)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => openConfirmTentative(appt)}>
                            Confirmar
                          </Button>
                          <Tooltip title="Eliminar tentativa">
                            <IconButton size="small" color="error" onClick={() => handleDeleteTentative(appt)}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={<Typography variant="body2" sx={{ fontWeight: 600 }}>{(appt.clientId?.name) || appt.leadName || 'Lead'}{appt.leadPhone ? ` · ${appt.leadPhone}` : ''}</Typography>}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                            <span>📅 {formatDate(appt.scheduledDate)}{appt.notes ? ` — ${appt.notes}` : ''}</span>
                            {appt.mapsLink && (
                              <Link
                                href={appt.mapsLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontWeight: 600, color: '#2e7d32' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <LocationIcon sx={{ fontSize: 16 }} /> Ver ubicación
                              </Link>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
              </List>
            </Paper>
          )}

          {/* Technician Legend */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Técnicos Disponibles
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {technicians.map((tech: any) => (
                <Chip
                  key={tech._id}
                  avatar={<Avatar sx={{ bgcolor: getTechnicianColor(tech._id), width: 24, height: 24 }}>{tech.name[0]}</Avatar>}
                  label={`${tech.name} - ${tech.email}`}
                  variant="outlined"
                  sx={{ borderColor: getTechnicianColor(tech._id) }}
                />
              ))}
            </Box>
          </Paper>

          <Grid container spacing={3}>
            {/* Calendar */}
            <Grid size={{ xs: 12, lg: 8 }}>
              <Paper sx={{ p: { xs: 1, sm: 3 }, height: { xs: 480, sm: 600 } }}>
                <Calendar
                  localizer={localizer}
                  events={calendarEvents}
                  startAccessor="start"
                  endAccessor="end"
                  view={view}
                  onView={setView}
                  date={date}
                  onNavigate={setDate}
                  onSelectEvent={handleSelectEvent}
                  onSelectSlot={handleSelectSlot}
                  selectable
                  eventPropGetter={eventStyleGetter}
                  messages={{
                    next: 'Siguiente',
                    previous: 'Anterior',
                    today: 'Hoy',
                    month: 'Mes',
                    week: 'Semana',
                    day: 'Día',
                    agenda: 'Agenda',
                    date: 'Fecha',
                    time: 'Hora',
                    event: 'Evento',
                    noEventsInRange: 'No hay citas en este rango',
                    showMore: (total) => `+ Ver ${total} más`,
                  }}
                  formats={{
                    timeGutterFormat: 'h:mm A',
                    eventTimeRangeFormat: ({ start, end }) =>
                      `${moment(start).format('h:mm A')} - ${moment(end).format('h:mm A')}`,
                    dayHeaderFormat: (date) => moment(date).format('dddd DD/MM'),
                    monthHeaderFormat: (date) => moment(date).format('MMMM YYYY'),
                  }}
                />
              </Paper>
            </Grid>

            {/* Appointments List */}
            <Grid size={{ xs: 12, lg: 4 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Próximas Citas
                </Typography>
                <List>
                  {appointments
                    .filter((apt: Appointment) => apt.status !== 'tentative' && new Date(apt.scheduledDate) >= new Date())
                    .sort((a: Appointment, b: Appointment) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
                    .slice(0, 5)
                    .map((appointment: Appointment, index: number) => {
                      const technician = technicians.find((t: any) => t._id === appointment.technicianId);
                      const showButtons = appointment.status === 'scheduled' || appointment.status === 'in_progress';
                      console.log(`📋 Cita ${appointment._id}: status="${appointment.status}", showButtons=${showButtons}`);
                      return (
                        <React.Fragment key={appointment._id}>
                          <ListItem alignItems="flex-start">
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: technician ? getTechnicianColor(technician._id) : '#2196F3' }}>
                                <ScheduleIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {(appointment.clientId as Client)?.name || 'Cliente'}
                                  </Typography>
                                  <Chip
                                    label={getStatusLabel(appointment.status)}
                                    color={getStatusColor(appointment.status) as any}
                                    size="small"
                                  />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    📅 {formatDate(appointment.scheduledDate)} - {to12h(appointment.timeSlot)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    👨‍🔧 {technician?.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    📍 {appointment.address.city}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color={appointment.deviceIds.length === 0 ? "error" : "text.secondary"}
                                    sx={{ fontWeight: appointment.deviceIds.length === 0 ? 600 : 400 }}
                                  >
                                    📦 {appointment.deviceIds.length === 0 ? '⚠️ Sin dispositivos asignados' : `${appointment.deviceIds.length} dispositivo(s)`}
                                  </Typography>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              {showButtons && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title={appointment.deviceIds.length === 0 ? "Agregar Dispositivos" : "Editar Cita"}>
                                    <IconButton
                                      edge="end"
                                      color={appointment.deviceIds.length === 0 ? "warning" : "default"}
                                      onClick={() => {
                                        setSelectedAppointment(appointment);
                                        setAppointmentDialogOpen(true);
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Completar Instalación">
                                    <IconButton
                                      edge="end"
                                      color="primary"
                                      onClick={() => handleCompleteInstallation(appointment)}
                                    >
                                      <CheckIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < 4 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                      );
                    })}
                </List>
              </Paper>

              {/* Quick Stats */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Estadísticas del Mes
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                          {appointments.filter((a: Appointment) => a.status === 'scheduled').length}
                        </Typography>
                        <Typography variant="caption">Programadas</Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                          {appointments.filter((a: Appointment) => a.status === 'completed').length}
                        </Typography>
                        <Typography variant="caption">Completadas</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Installations Tab */}
      {currentTab === 1 && (
        <Grid container spacing={3}>
          {/* Installations List */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Instalaciones Completadas
              </Typography>
              {installationsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : installationsError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Error cargando instalaciones: {installationsError}
                </Alert>
              ) : (
                <List>
                  {installations.map((installation: any, index: number) => {
                    // technicianId viene poblado como objeto {_id, name...}; si no, buscar por id
                    const technician = (installation.technicianId && typeof installation.technicianId === 'object')
                      ? installation.technicianId
                      : technicians.find((t: any) => t._id === installation.technicianId);
                    // el campo real es deviceId (singular); deviceIds puede no existir
                    const deviceCount = installation.deviceIds?.length || (installation.deviceId ? 1 : 0);
                    return (
                      <React.Fragment key={installation._id}>
                        <ListItem alignItems="flex-start">
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: installation.status === 'installed' ? '#4CAF50' : '#FF9800' }}>
                              <InstallationIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {(installation.clientId as Client)?.name || 'Cliente'}
                                </Typography>
                                <Chip
                                  label={installation.status === 'installed' ? 'Instalado' : installation.status}
                                  color={installation.status === 'installed' ? 'success' : 'warning'}
                                  size="small"
                                />
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  📅 {formatDate(installation.installationDate)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  👨‍🔧 {technician?.name || 'Técnico no encontrado'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  📦 {deviceCount} dispositivo(s)
                                </Typography>
                                {installation.commission && (
                                  <Typography variant="body2" color="text.secondary">
                                    💰 Comisión: ${installation.commission.amount}
                                    {installation.commission.isPaid ? ' (Pagada)' : ' (Pendiente)'}
                                  </Typography>
                                )}
                                {installation.notes && (
                                  <Typography variant="body2" color="text.secondary">
                                    📝 {installation.notes}
                                  </Typography>
                                )}
                                {(() => {
                                  const pi = installation.paymentInfo;
                                  const confirmed = pi && pi.method && pi.method !== 'none';
                                  return confirmed ? (
                                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                                      💵 Pago: {pi.method === 'cash' ? 'Efectivo' : 'Transferencia'} RD${Number(pi.amount || 0).toLocaleString('es-DO')} ✓
                                    </Typography>
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      startIcon={<PaymentIcon />}
                                      sx={{ mt: 0.5 }}
                                      onClick={() => openConfirmPayment(installation)}
                                    >
                                      Confirmar pago
                                    </Button>
                                  );
                                })()}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            {installation.commission && !installation.commission.isPaid && (
                              <Tooltip title="Marcar comisión como pagada">
                                <IconButton edge="end" color="success">
                                  <PaymentIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < installations.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                    );
                  })}
                  {installations.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No hay instalaciones completadas
                      </Typography>
                    </Box>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Installation Stats */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Estadísticas de Instalaciones
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                        {installations.filter((i: any) => i.status === 'installed').length}
                      </Typography>
                      <Typography variant="caption">Instalaciones Exitosas</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                      <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                        {installations.filter((i: any) => i.commission && !i.commission.isPaid).length}
                      </Typography>
                      <Typography variant="caption">Comisiones Pendientes</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                      <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                        ${installations
                          .filter((i: any) => i.commission && !i.commission.isPaid)
                          .reduce((sum: number, i: any) => sum + (i.commission?.amount || 0), 0)
                          .toFixed(2)}
                      </Typography>
                      <Typography variant="caption">Total Comisiones Pendientes</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Top Technicians */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Rendimiento de Técnicos
                </Typography>
                <List dense>
                  {technicians.map((tech: any) => {
                    const techInstallations = installations.filter((i: any) => (i.technicianId?._id || i.technicianId) === tech._id);
                    const successfulInstallations = techInstallations.filter((i: any) => i.status === 'installed');
                    const successRate = techInstallations.length > 0
                      ? (successfulInstallations.length / techInstallations.length) * 100
                      : 0;

                    return (
                      <ListItem key={tech._id} dense>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: getTechnicianColor(tech._id), width: 32, height: 32 }}>
                            {tech.name.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={tech.name}
                          secondary={
                            <Box>
                              <Typography variant="caption" component="div">
                                {techInstallations.length} instalaciones
                              </Typography>
                              <Typography variant="caption" component="div">
                                {successRate.toFixed(1)}% éxito
                              </Typography>
                            </Box>
                          }
                        />
                        <Badge
                          badgeContent={successfulInstallations.length}
                          color="success"
                          sx={{ mr: 2 }}
                        >
                          <StarIcon color="action" />
                        </Badge>
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Appointment Dialog */}
      <AppointmentDialog
        open={appointmentDialogOpen}
        appointment={selectedAppointment}
        onClose={() => setAppointmentDialogOpen(false)}
        onSubmit={handleAppointmentSubmit}
        onComplete={handleCompleteInstallation}
        loading={creating || updating}
        technicians={technicians}
      />

      {/* Complete Installation Dialog */}
      <Dialog
        open={completeInstallationDialog.open}
        onClose={() => {
          setCompleteInstallationDialog({ open: false, appointment: null });
          setCloseAppointmentData({ clientId: '', completionNotes: '', deviceIds: [], deviceNames: [], paymentMethod: 'none', paymentAmount: '', paymentReference: '' });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {completeInstallationDialog.quick && <InstallationIcon color="success" />}
          {completeInstallationDialog.quick ? 'Instalación Rápida' : 'Cerrar Cita y Completar Instalación'}
        </DialogTitle>
        <DialogContent>
          {(completeInstallationDialog.appointment || completeInstallationDialog.quick) && (
            <>
              {completeInstallationDialog.appointment ? (
                <Box sx={{ mb: 3, mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Información de la Cita
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">Técnico:</Typography>
                      <Typography variant="body1">
                        {completeInstallationDialog.appointment.technicianName}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">Fecha:</Typography>
                      <Typography variant="body1">
                        {formatDate(completeInstallationDialog.appointment.scheduledDate)} - {to12h(completeInstallationDialog.appointment.timeSlot)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary">Dirección:</Typography>
                      <Typography variant="body1">
                        {completeInstallationDialog.appointment.address.street}, {completeInstallationDialog.appointment.address.city}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary">Dispositivos:</Typography>
                      <Typography variant="body1">
                        El técnico seleccionará los dispositivos instalados a continuación
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ mb: 3, mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Datos de la Instalación
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Registra una instalación hecha sin cita previa. Al completar se crea el usuario del cliente, se envía el correo con las credenciales, se transfiere el dispositivo y se aplica la comisión — igual que al completar una cita.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth required>
                        <InputLabel>Técnico (recibe comisión)</InputLabel>
                        <Select
                          value={completeInstallationDialog.technicianId || ''}
                          label="Técnico (recibe comisión)"
                          onChange={(e) => setCompleteInstallationDialog(prev => ({ ...prev, technicianId: e.target.value }))}
                        >
                          {technicians.map((t: any) => (
                            <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Fecha de la instalación"
                        InputLabelProps={{ shrink: true }}
                        value={completeInstallationDialog.scheduledDate || ''}
                        onChange={(e) => setCompleteInstallationDialog(prev => ({ ...prev, scheduledDate: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 3 }}>
                Asignar Cliente y Completar
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Al completar esta cita, se realizará automáticamente:
                <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                  <li>Creación de usuario Traccar para el cliente (si no existe)</li>
                  <li>Transferencia de dispositivos del técnico al cliente</li>
                  <li>Envío de credenciales de acceso por email</li>
                  <li>Registro de la instalación como completada</li>
                </ul>
              </Alert>

              {/* Tabs para Crear Nuevo o Seleccionar Existente */}
              <Paper variant="outlined" sx={{ mb: 3 }}>
                <Tabs
                  value={clientSelectionTab}
                  onChange={(_, newValue) => setClientSelectionTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab label="Crear Nuevo Cliente" icon={<AddIcon />} iconPosition="start" />
                  <Tab label="Seleccionar Existente" icon={<PersonIcon />} iconPosition="start" />
                </Tabs>

                <Box sx={{ p: 3 }}>
                  {/* Tab 0: Crear Nuevo Cliente */}
                  {clientSelectionTab === 0 && (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          required
                          label="Nombre Completo"
                          value={newClientData.name}
                          onChange={(e) => setNewClientData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Juan Pérez"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          required
                          label="Email"
                          type="email"
                          value={newClientData.email}
                          onChange={(e) => setNewClientData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="cliente@email.com"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Teléfono"
                          value={newClientData.phoneNumber}
                          onChange={(e) => setNewClientData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                          placeholder="+1 809-555-1234"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Ciudad"
                          value={newClientData.city}
                          onChange={(e) => setNewClientData(prev => ({ ...prev, city: e.target.value }))}
                          placeholder="Santo Domingo"
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Dirección"
                          value={newClientData.address}
                          onChange={(e) => setNewClientData(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="Calle Principal #123, Sector"
                        />
                      </Grid>
                    </Grid>
                  )}

                  {/* Tab 1: Seleccionar Cliente Existente */}
                  {clientSelectionTab === 1 && (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <Autocomplete
                          options={clients}
                          loading={clientsLoading}
                          getOptionLabel={(option: any) => `${option.name} - ${option.email} - ${option.phoneNumber || 'Sin teléfono'}`}
                          value={clients.find((c: any) => c._id === closeAppointmentData.clientId) || null}
                          onChange={(_, newValue) => {
                            setCloseAppointmentData(prev => ({
                              ...prev,
                              clientId: newValue?._id || ''
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Buscar Cliente"
                              placeholder="Buscar por nombre, email o teléfono..."
                              helperText={clients.length === 0 ? "No hay clientes registrados. Use la opción 'Crear Nuevo Cliente'." : ""}
                            />
                          )}
                          renderOption={(props, option: any) => (
                            <li {...props}>
                              <Box>
                                <Typography variant="body1">{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.email} {option.phoneNumber ? `• ${option.phoneNumber}` : ''}
                                </Typography>
                              </Box>
                            </li>
                          )}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Paper>

              {/* Selección de Dispositivos (REQUERIDO) */}
              <Paper variant="outlined" sx={{ mb: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Dispositivos Instalados
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Seleccione los dispositivos que fueron instalados. Si uno no aparece en la lista
                  (recién instalado), agréguelo por su IMEI y se traerá de Traccar.
                </Alert>
                {/* Agregar por IMEI: trae el equipo de Traccar aunque no esté en el grupo stock */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Agregar equipo por IMEI (completo o últimos 4+ dígitos)"
                    value={imeiToAdd}
                    onChange={(e) => setImeiToAdd(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDeviceByImei(); } }}
                    placeholder="Ej: 866557088919628"
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddDeviceByImei}
                    disabled={addingImei || imeiToAdd.trim().length < 4}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {addingImei ? 'Buscando…' : 'Agregar'}
                  </Button>
                </Box>
                <Autocomplete
                  multiple
                  options={availableDevices}
                  loading={notInstalledLoading}
                  getOptionLabel={(option: any) => `${option.imei} - ${option.model || 'N/A'}`}
                  value={availableDevices.filter((d: any) =>
                    closeAppointmentData.deviceIds.includes(d.traccarId || d.id)
                  )}
                  onChange={(_, newValue) => {
                    const selectedIds = newValue.map((d: any) => d.traccarId || d.id);
                    setCloseAppointmentData(prev => ({
                      ...prev,
                      deviceIds: selectedIds,
                      deviceNames: Array(selectedIds.length).fill('')
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar Dispositivos"
                      placeholder="Buscar por IMEI..."
                      error={closeAppointmentData.deviceIds.length === 0}
                      helperText={
                        closeAppointmentData.deviceIds.length === 0
                          ? 'Debe seleccionar al menos un dispositivo'
                          : `${closeAppointmentData.deviceIds.length} dispositivo(s) seleccionado(s)`
                      }
                    />
                  )}
                  renderOption={(props, option: any) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2">IMEI: {option.imei}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.model || 'N/A'} • TraccarID: {option.traccarId || option.id}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Paper>

              {/* Nombres de Dispositivos (OBLIGATORIO) */}
              {closeAppointmentData.deviceIds.length > 0 && (
                <Paper variant="outlined" sx={{ mb: 3, p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Nombres de Dispositivos (Requerido)
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    Debe proporcionar un nombre para cada dispositivo. Se configurará expiración automática de 1 año.
                  </Alert>
                  <Grid container spacing={2}>
                    {closeAppointmentData.deviceIds.map((traccarId: number, index: number) => {
                      const device = availableDevices.find((d: any) => (d.traccarId || d.id) === traccarId);
                      return (
                        <Grid key={index} size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            required
                            label={`Nombre Dispositivo ${index + 1} - IMEI: ${device?.imei || 'N/A'}`}
                            value={closeAppointmentData.deviceNames[index] || ''}
                            onChange={(e) => {
                              const newNames = [...closeAppointmentData.deviceNames];
                              newNames[index] = e.target.value;
                              setCloseAppointmentData(prev => ({
                                ...prev,
                                deviceNames: newNames
                              }));
                            }}
                            placeholder={`Ej: Vehículo ${device?.name || device?.model || ''}`}
                            helperText="Este nombre se mostrará en Traccar (ej: Camioneta Ford)"
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              )}

              {/* Sección de Pago del Cliente */}
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon color="primary" />
                  Pago del Cliente
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>¿Quién recibió el dinero?</InputLabel>
                      <Select
                        value={closeAppointmentData.paymentMethod}
                        label="¿Quién recibió el dinero?"
                        onChange={(e) => setCloseAppointmentData(prev => ({
                          ...prev,
                          paymentMethod: e.target.value as MetodoCobro
                        }))}
                      >
                        {/* Se nombra a quién le llegó, no el instrumento: eso es lo que decide
                            si el técnico se lo debe a la empresa. */}
                        <MenuItem value="none">Sin pago / Pendiente</MenuItem>
                        <MenuItem value="cash">Efectivo al técnico</MenuItem>
                        <MenuItem value="transfer_technician">Transferencia a la cuenta del técnico</MenuItem>
                        <MenuItem value="transfer_company">Transferencia a Driports</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Monto (DOP)"
                      type="number"
                      value={closeAppointmentData.paymentAmount}
                      onChange={(e) => setCloseAppointmentData(prev => ({
                        ...prev,
                        paymentAmount: e.target.value
                      }))}
                      disabled={closeAppointmentData.paymentMethod === 'none'}
                      placeholder="0"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Referencia (opcional)"
                      value={closeAppointmentData.paymentReference}
                      onChange={(e) => setCloseAppointmentData(prev => ({
                        ...prev,
                        paymentReference: e.target.value
                      }))}
                      disabled={closeAppointmentData.paymentMethod === 'none'}
                      placeholder="# Transferencia, recibo, etc."
                    />
                  </Grid>
                </Grid>
                {closeAppointmentData.paymentAmount
                  && (closeAppointmentData.paymentMethod === 'cash'
                      || closeAppointmentData.paymentMethod === 'transfer_technician') && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    El dinero queda con el técnico:{' '}
                    <strong>{parseFloat(closeAppointmentData.paymentAmount).toLocaleString('es-DO')} DOP</strong>{' '}
                    se le suman a lo que tiene que entregar.
                  </Alert>
                )}
                {closeAppointmentData.paymentAmount && closeAppointmentData.paymentMethod === 'transfer_company' && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Entra directo a la cuenta de Driports; no se le carga al técnico.
                  </Alert>
                )}
              </Paper>

              {/* Notas de Completación (común para ambos tabs) */}
              <TextField
                fullWidth
                label="Notas de Completación (opcional)"
                multiline
                rows={3}
                value={closeAppointmentData.completionNotes}
                onChange={(e) => setCloseAppointmentData(prev => ({
                  ...prev,
                  completionNotes: e.target.value
                }))}
                placeholder="Comentarios sobre la instalación, problemas encontrados, etc..."
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCompleteInstallationDialog({ open: false, appointment: null });
              setCloseAppointmentData({ clientId: '', completionNotes: '', deviceIds: [], deviceNames: [], paymentMethod: 'none', paymentAmount: '', paymentReference: '' });
            }}
            disabled={completing}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={completeInstallationDialog.quick ? 'success' : 'primary'}
            onClick={handleCompleteInstallationSubmit}
            disabled={
              completing ||
              (completeInstallationDialog.quick && !completeInstallationDialog.technicianId) ||
              (clientSelectionTab === 0 && (!newClientData.name || !newClientData.email)) ||
              (clientSelectionTab === 1 && !closeAppointmentData.clientId)
            }
            startIcon={completing ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {completing ? 'Procesando...' : (completeInstallationDialog.quick ? 'Completar Instalación' : 'Completar y Cerrar Cita')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar cita tentativa */}
      <Dialog open={confirmTentativeDialog.open} onClose={() => setConfirmTentativeDialog({ open: false, appt: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckIcon color="success" /> Confirmar cita
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Confirma la cita de <strong>{confirmTentativeDialog.appt?.clientId?.name || confirmTentativeDialog.appt?.leadName || 'este lead'}</strong> asignándole un técnico. Pasará de tentativa a agendada.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Técnico</InputLabel>
              <Select value={tentTechnicianId} label="Técnico" onChange={(e) => setTentTechnicianId(e.target.value)}>
                {technicians.map((t: any) => (
                  <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Hora (opcional)" type="time" fullWidth value={tentTime}
              onChange={(e) => setTentTime(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="Ubicación (opcional)" fullWidth value={tentLocation}
              onChange={(e) => setTentLocation(e.target.value)}
              placeholder="Pega el link de Google Maps o 18.53, -69.88"
              helperText={tentLocation && !parseLatLng(tentLocation)
                ? 'No detecté coordenadas. Pega el link completo de Maps o lat, lng.'
                : 'Si el cliente compartió ubicación por WhatsApp ya viene aquí. El técnico la recibe como pin.'}
              error={!!tentLocation && !parseLatLng(tentLocation)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmTentativeDialog({ open: false, appt: null })} disabled={tentSaving}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleConfirmTentative} disabled={!tentTechnicianId || tentSaving}>
            {tentSaving ? 'Confirmando...' : 'Confirmar cita'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Editar / reprogramar una cita tentativa */}
      <Dialog open={editTentDialog.open} onClose={() => setEditTentDialog({ open: false, appt: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Editar cita tentativa</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reprograma la tentativa (la gente cambia de opinión). Sigue siendo tentativa hasta confirmarla con un técnico.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 7 }}>
              <TextField fullWidth type="date" label="Fecha tentativa" InputLabelProps={{ shrink: true }} required
                value={editTentForm.scheduledDate}
                onChange={(e) => setEditTentForm(p => ({ ...p, scheduledDate: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 5 }}>
              <TextField fullWidth type="time" label="Hora (opcional)" InputLabelProps={{ shrink: true }}
                value={editTentForm.timeSlot}
                onChange={(e) => setEditTentForm(p => ({ ...p, timeSlot: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Nombre del lead"
                value={editTentForm.leadName}
                onChange={(e) => setEditTentForm(p => ({ ...p, leadName: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Teléfono"
                value={editTentForm.leadPhone}
                onChange={(e) => setEditTentForm(p => ({ ...p, leadPhone: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline rows={2} label="Nota (opcional)"
                value={editTentForm.notes}
                onChange={(e) => setEditTentForm(p => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTentDialog({ open: false, appt: null })} disabled={editTentSaving}>Cancelar</Button>
          <Button variant="contained" onClick={handleEditTentative} disabled={!editTentForm.scheduledDate || editTentSaving}>
            {editTentSaving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar pago del cliente (post-instalación) */}
      <Dialog open={payDialog.open} onClose={() => setPayDialog({ open: false, installation: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar pago del cliente</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {(payDialog.installation?.clientId as any)?.name || 'Cliente'} — este pago quedará registrado y <strong>no se podrá modificar</strong>.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Método de pago</InputLabel>
            <Select
              value={payMethod}
              label="Método de pago"
              onChange={(e) => setPayMethod(e.target.value as 'cash' | 'bank_transfer')}
            >
              <MenuItem value="cash">Efectivo</MenuItem>
              <MenuItem value="bank_transfer">Transferencia</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            label="Monto (RD$)"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Referencia (opcional)"
            placeholder={payMethod === 'bank_transfer' ? 'No. de transferencia' : 'Opcional'}
            value={payReference}
            onChange={(e) => setPayReference(e.target.value)}
          />
          {payMethod === 'cash' && parseFloat(payAmount) > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              El técnico recibió <strong>{parseFloat(payAmount).toLocaleString('es-DO')} DOP</strong> en efectivo y se le sumará a lo que le debe a la empresa.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog({ open: false, installation: null })}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleConfirmPayment} disabled={!(parseFloat(payAmount) > 0) || paySaving}>
            {paySaving ? 'Confirmando...' : 'Confirmar pago'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add appointment"
        onClick={() => {
          setSelectedAppointment(null);
          setAppointmentDialogOpen(true);
        }}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', lg: 'none' },
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default CalendarPage;